import * as React from 'react'
import { GripVertical, MoreHorizontal } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { CustomField } from '@/lib/contacts/types'

export function CustomFieldsTable({
  customFields,
  onEdit,
  onReorder,
  reorderPending,
  onSetActive,
  onDelete,
}: {
  customFields: CustomField[]
  onEdit: (field: CustomField) => void
  onReorder: (customFields: CustomField[]) => void
  reorderPending: boolean
  onSetActive: (field: CustomField, active: boolean) => void
  onDelete: (field: CustomField) => void
}) {
  const [draggingFieldId, setDraggingFieldId] = React.useState<number | null>(null)

  function reorderTo(targetField: CustomField) {
    if (draggingFieldId === null || draggingFieldId === targetField.id) return

    const nextCustomFields = reorderedCustomFields(customFields, draggingFieldId, targetField.id)
    setDraggingFieldId(null)
    if (nextCustomFields) onReorder(nextCustomFields)
  }

  return (
    <Table className="min-w-[920px]">
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Label</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Applies to</TableHead>
          <TableHead>Field access</TableHead>
          <TableHead>Join form</TableHead>
          <TableHead>Required</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-20" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {customFields.map((field) => (
          <TableRow
            key={field.id}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => reorderTo(field)}
          >
            <TableCell>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 cursor-grab p-0"
                aria-label={`Drag to reorder ${field.label}`}
                draggable
                disabled={reorderPending}
                onDragStart={() => setDraggingFieldId(field.id)}
              >
                <GripVertical className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TableCell>
            <TableCell className="text-foreground">{field.label}</TableCell>
            <TableCell className="text-muted-foreground">{field.field_type}</TableCell>
            <TableCell className="text-muted-foreground">{field.applies_to}</TableCell>
            <TableCell className="text-muted-foreground">{field.field_access}</TableCell>
            <TableCell>
              <Badge variant={field.ask_on_join_form ? 'success' : 'neutral'}>
                {field.ask_on_join_form ? 'Asked' : 'Not asked'}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant={field.required ? 'success' : 'neutral'}>
                {field.required ? 'Required' : 'Optional'}
              </Badge>
            </TableCell>
            <TableCell>
              <Badge variant={field.active ? 'success' : 'neutral'}>
                {field.active ? 'Active' : 'Inactive'}
              </Badge>
            </TableCell>
            <TableCell className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={`Custom Field actions for ${field.label}`}
                  >
                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => onEdit(field)}>Edit</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onSetActive(field, !field.active)}>
                    {field.active ? 'Deactivate' : 'Reactivate'}
                  </DropdownMenuItem>
                  <DropdownMenuItem destructive onSelect={() => onDelete(field)}>
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function reorderedCustomFields(
  customFields: CustomField[],
  draggingFieldId: number,
  targetFieldId: number,
) {
  const fromIndex = customFields.findIndex((field) => field.id === draggingFieldId)
  const toIndex = customFields.findIndex((field) => field.id === targetFieldId)
  if (fromIndex < 0 || toIndex < 0) return null

  const nextCustomFields = [...customFields]
  const [draggedField] = nextCustomFields.splice(fromIndex, 1)
  nextCustomFields.splice(toIndex, 0, draggedField)
  return nextCustomFields
}
